import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { formatCuit } from "../../crmpostventa-shared/crmFormat";
import { SEARCH_MIN_LENGTH, plural } from "../../crmpostventa-rollo/nuevaGestion/nuevaGestionModel";

const SKELETON_WIDTHS = [
  ["38%", "78%"],
  ["52%", "64%"],
  ["30%", "82%"],
  ["44%", "70%"],
];

function Note({ error = false, icon, title, children }) {
  return (
    <div className={`crm-rollo__note${error ? " crm-rollo__note--error" : ""}`} role={error ? "alert" : undefined}>
      <span className="crm-rollo__note-ico" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>
      <b>{title}</b>
      {children}
    </div>
  );
}

function Skeleton({ label }) {
  return (
    <div className="crm-rollo__rcards" aria-busy="true" aria-label={label}>
      {SKELETON_WIDTHS.map(([title, meta]) => (
        <div key={title} className="crm-rollo__rcard" aria-hidden="true">
          <span className="crm-rollo__skel crm-rollo__skel--radio" />
          <div className="crm-rollo__rcard-skel">
            <span className="crm-rollo__skel crm-rollo__skel--tall" style={{ width: title }} />
            <span className="crm-rollo__skel" style={{ width: meta }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ComercioCard({ comercio, selected, onSelect, onConfirm }) {
  return (
    <label className={`crm-rollo__rcard${selected ? " is-selected" : ""}`} onDoubleClick={() => onConfirm(comercio.key)}>
      <input type="radio" name="crm-ipc-comercio" value={comercio.key} checked={selected} onChange={() => onSelect(comercio.key)} />
      <div>
        <div className="crm-rollo__rcard-name">{comercio.nombre || comercio.razonSocial || "Sin nombre"}</div>
        {comercio.activo ? (
          <div className="crm-ipc__activo">
            <span className="crm-rollo__chip crm-ipc__chip--activo">Comercio activo</span>
          </div>
        ) : null}
        <div className="crm-rollo__rcard-meta">
          {comercio.razonSocial ? <span>{comercio.razonSocial}</span> : null}
          {comercio.cuit ? <span>{`CUIT ${formatCuit(comercio.cuit)}`}</span> : null}
          {comercio.numeroComercio ? <span>{`N° ${comercio.numeroComercio}`}</span> : null}
          {comercio.terminales ? <span>{plural(comercio.terminales, "terminal", "terminales")}</span> : null}
        </div>
      </div>
    </label>
  );
}

function ComercioCards({ comercios, label, selectedKey, onSelect, onConfirm }) {
  return (
    <>
      <div className="crm-rollo__list-meta">{label}</div>
      <fieldset className="crm-rollo__rcards" role="radiogroup" aria-label="Comercios">
        <legend className="crm-rollo__sr-only">Comercios</legend>
        {comercios.map((comercio) => (
          <ComercioCard
            key={comercio.key}
            comercio={comercio}
            selected={selectedKey === comercio.key}
            onSelect={onSelect}
            onConfirm={onConfirm}
          />
        ))}
      </fieldset>
    </>
  );
}

/**
 * Step 1 · Seleccionar comercio (design Prompt 2, the same in Proactiva). Controlled by NuevaGestionIpcModal, which
 * owns the search so the query, results and selection survive "Cambiar comercio". Without text it shows "Comercios
 * recientes": the CRM active comercio first, marked "Comercio activo" (Prompt 7, point 4); step 1 is never skipped.
 * When the active comercio cannot be loaded, a short notice with "Reintentar" (onRetryActivo) shows above the search
 * hint.
 */
export default function ComercioStep({
  proactiva,
  inputRef,
  query,
  onQueryChange,
  search,
  activo,
  selectedKey,
  onSelect,
  onConfirm,
  onRetry,
  onRetryActivo,
}) {
  const renderRecientes = () => {
    if (activo.status === "loading") return <Skeleton label="Cargando comercios recientes" />;
    if (activo.card) {
      return <ComercioCards comercios={[activo.card]} label="Comercios recientes" selectedKey={selectedKey} onSelect={onSelect} onConfirm={onConfirm} />;
    }
    return (
      <>
        {activo.status === "error" ? (
          <div className="crm-ipc__activo-error" role="alert">
            <span>No pudimos cargar el comercio activo.</span>
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={onRetryActivo}>
              <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
              Reintentar
            </button>
          </div>
        ) : null}
        <Note icon={faMagnifyingGlass} title={proactiva ? "Buscá el comercio que vas a contactar" : "Buscá el comercio que se comunicó"}>
          <p>{`Escribí al menos ${SEARCH_MIN_LENGTH} caracteres del nombre, CUIT, número de comercio, terminal o razón social.`}</p>
        </Note>
      </>
    );
  };

  const renderResults = () => {
    if (search.status === "idle") return renderRecientes();
    if (search.status === "loading") return <Skeleton label="Buscando comercios" />;

    if (search.status === "error") {
      return (
        <Note error icon={faTriangleExclamation} title="No pudimos buscar comercios">
          <p>Hubo un problema de conexión con el servidor. Probá de nuevo en unos segundos.</p>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={onRetry}>
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Reintentar
          </button>
        </Note>
      );
    }

    if (!search.results.length) {
      return (
        <Note icon={faMagnifyingGlass} title={`No encontramos comercios para “${search.text}”`}>
          <p>Probá con el CUIT sin guiones, el número de comercio, la terminal o la razón social.</p>
        </Note>
      );
    }

    return (
      <ComercioCards
        comercios={search.results}
        label={plural(search.results.length, "resultado", "resultados")}
        selectedKey={selectedKey}
        onSelect={onSelect}
        onConfirm={onConfirm}
      />
    );
  };

  return (
    <>
      <label className="crm-rollo__field crm-rollo__search crm-rollo__modal-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="crm-rollo__search-ico" aria-hidden="true" />
        <input
          ref={inputRef}
          className="crm-rollo__input"
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Buscar por nombre, CUIT, número de comercio, terminal o razón social"
          aria-label="Buscar comercio"
          autoComplete="off"
        />
      </label>
      <div className="crm-rollo__picker" aria-live="polite">
        {renderResults()}
      </div>
    </>
  );
}
